import type {
  AutomationsResponse,
  DownloadTicket,
  ExplanationResponse,
  ProblemsResponse,
  ResultsResponse,
  RunsResponse,
  SessionInfo,
  UploadsResponse,
  WebAutomation
} from '@shared/web'
import type { ProblemFile, ResultFile, RunSummary, UploadOutcome } from '@shared/types'

/** Fired on window when any call fails because the session is gone. */
export const AUTH_EXPIRED_EVENT = 'autom8:auth-expired'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface RequestOptions extends RequestInit {
  /**
   * Suppresses the auth-expired event on a 401. Used by the startup session
   * probe, where a 401 is the ordinary answer for someone who has simply not
   * signed in yet — announcing "your session expired" to a first-time visitor
   * is both wrong and alarming.
   */
  silentAuth?: boolean
}

/**
 * The single place this app talks to the broker. Cookies are same-origin and
 * HttpOnly, so there is no token for this module to hold or forget to clear —
 * signing out is a request, not a bit of local state.
 */
async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const { silentAuth = false, ...fetchInit } = init
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      ...fetchInit,
      credentials: 'same-origin',
      headers: {
        ...(fetchInit.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...fetchInit.headers
      }
    })
  } catch {
    throw new ApiError('Could not reach Autom8. Check your connection and try again.', 0)
  }

  if (res.status === 204) return undefined as T

  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* an error page with no JSON body */
  }

  if (!res.ok) {
    const parsed = (body ?? {}) as { error?: string; code?: string }
    if (parsed.code === 'auth-expired' && !silentAuth) {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
    }
    throw new ApiError(parsed.error ?? 'Something went wrong.', res.status)
  }
  return body as T
}

// --- what the sign-in button needs before anyone is signed in ---

export interface PublicConfig {
  googleClientId: string
  hostedDomain?: string
}

export function getPublicConfig(): Promise<PublicConfig> {
  return request<PublicConfig>('/config')
}

// --- session ---

/**
 * Who is signed in. A 401 here means "nobody yet", which is the normal state
 * on a first visit — so it must not be reported as an expired session.
 */
export function getSession(): Promise<SessionInfo> {
  return request<SessionInfo>('/session', { silentAuth: true })
}

export function signIn(credential: string): Promise<SessionInfo> {
  return request<SessionInfo>('/session', {
    method: 'POST',
    body: JSON.stringify({ credential })
  })
}

export function signOut(): Promise<void> {
  return request<void>('/session', { method: 'DELETE' })
}

// --- automations ---

export async function listAutomations(refresh = false): Promise<WebAutomation[]> {
  const { automations } = await request<AutomationsResponse>(
    `/automations${refresh ? '?refresh=1' : ''}`
  )
  return automations
}

export function runNow(jobId: string): Promise<void> {
  return request<void>(`/automations/${encodeURIComponent(jobId)}/run`, { method: 'POST' })
}

export async function listRuns(jobId: string): Promise<RunSummary[]> {
  const { runs } = await request<RunsResponse>(`/automations/${encodeURIComponent(jobId)}/runs`)
  return runs
}

export async function listResults(jobId: string): Promise<ResultFile[]> {
  const { files } = await request<ResultsResponse>(`/automations/${encodeURIComponent(jobId)}/results`)
  return files
}

export async function listProblems(jobId: string): Promise<ProblemFile[]> {
  const { files } = await request<ProblemsResponse>(
    `/automations/${encodeURIComponent(jobId)}/problems`
  )
  return files
}

export async function explanation(jobId: string, object: string): Promise<string> {
  const { text } = await request<ExplanationResponse>(
    `/automations/${encodeURIComponent(jobId)}/explanation?object=${encodeURIComponent(object)}`
  )
  return text
}

/** Hands the browser a URL to save under a given file name. */
function saveAs(url: string, name: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * Starts a download. The broker mints a short-lived signed URL for the one
 * object, and the browser fetches it straight from Cloud Storage — the file
 * bytes never pass through the app or the broker.
 *
 * `confirmReceipt` is for automations set to keep no copy, and changes how the
 * file is fetched. Normally the anchor below hands the URL to the browser and
 * we never hear about it again, which is fine when nothing depends on the
 * outcome — but it cannot tell a saved file from a failed one, and the broker
 * is about to delete the only copy. So the bytes are fetched here first: a
 * resolved blob is proof the transfer completed, and only then is the deletion
 * confirmed. It costs holding the file in memory, which is why it is not the
 * default.
 *
 * Returns what became of the cloud copies.
 */
export interface DownloadResult {
  deleted: boolean
  /** The automation keeps no copy, but the cloud copies could not be removed. */
  cleanupFailed?: boolean
}

export async function download(
  jobId: string,
  object: string,
  confirmReceipt = false
): Promise<DownloadResult> {
  const ticket = await request<DownloadTicket>(
    `/automations/${encodeURIComponent(jobId)}/download?object=${encodeURIComponent(object)}`
  )
  if (!confirmReceipt) {
    saveAs(ticket.url, ticket.name)
    return { deleted: false }
  }

  let blob: Blob
  try {
    const res = await fetch(ticket.url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    blob = await res.blob()
  } catch {
    throw new ApiError('That file could not be downloaded. Nothing was deleted.', 0)
  }

  const objectUrl = URL.createObjectURL(blob)
  saveAs(objectUrl, ticket.name)
  // Revoking immediately can cancel a save the browser has not finished
  // writing; the blob is freed a minute later instead.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)

  // The file is saved by now, so a confirm that never lands is not a failed
  // download — it is a copy left behind, which is what the caller is told.
  try {
    return await request<DownloadResult>(`/automations/${encodeURIComponent(jobId)}/downloaded`, {
      method: 'POST',
      body: JSON.stringify({ object })
    })
  } catch {
    return { deleted: false, cleanupFailed: true }
  }
}

/**
 * Uploads files to an automation's incoming folder.
 *
 * Two steps on purpose: the broker decides *whether* and *where* each file may
 * go and returns a signed policy for exactly that object, then the browser
 * posts the bytes straight to Cloud Storage. Nothing large crosses the broker,
 * and the policy's own conditions cap the size and content type even if this
 * code is wrong about them.
 */
export async function sendFiles(
  jobId: string,
  files: File[],
  slotId: string | null
): Promise<UploadOutcome[]> {
  const { tickets } = await request<UploadsResponse>(
    `/automations/${encodeURIComponent(jobId)}/uploads`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(slotId ? { slotId } : {}),
        files: files.map((f) => ({ name: f.name, size: f.size }))
      })
    }
  )

  const byName = new Map(files.map((f) => [f.name, f]))
  const outcomes: UploadOutcome[] = []
  for (const ticket of tickets) {
    if (!ticket.ok) {
      outcomes.push({ name: ticket.name, ok: false, error: ticket.error })
      continue
    }
    const file = byName.get(ticket.name)
    if (!file) continue
    try {
      const form = new FormData()
      for (const [key, value] of Object.entries(ticket.fields)) form.append(key, value)
      // The file part must come last: Cloud Storage ignores fields that follow it.
      form.append('file', file)
      const res = await fetch(ticket.url, { method: 'POST', body: form })
      if (!res.ok) throw new Error(`upload failed with HTTP ${res.status}`)
      outcomes.push({ name: ticket.name, ok: true })
    } catch {
      // Deliberately not only "check your connection": the most common cause
      // of a failure here is not the network but the bucket having no CORS
      // policy for this origin, which blocks the browser's POST before it
      // leaves. Naming both saves an administrator from debugging the wrong
      // thing — see the troubleshooting entry in deploy/README.md.
      outcomes.push({
        name: ticket.name,
        ok: false,
        error:
          'That file could not be uploaded. Check your connection — and if it keeps happening, ' +
          'ask your administrator to check the bucket’s CORS settings.'
      })
    }
  }
  return outcomes
}

// --- formatting ---

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function formatWhen(iso: string | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}
